import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";

import CommonButton from "../../utilities/formElements/commonButton/CommonButton";
import { loginValidationSchema } from "../../utilities/validationSchema";
import InputWrapper from "../../utilities/formElements/commonInput/InputWrapper";
import CommonModel from "../../utilities/commonModel";
import { toastMessageError } from "../../utilities/commonToastMessage";

import storage from "../../../util/storage";

import { ILogin } from "../../../Interfaces/AuthInterface";

import { login } from "../../../services/auth";

import ActionType from "../../../resources/enums";
import ROUTES from "../../../constants/routes";
import { typePassword, typeText } from "../../../constants/commonConstants";

import Logo from "../../../assets/images/Skydive_logo_beta.svg";
import EyeIcon from "../../../assets/images/eye-outline.svg";
import EyeCloseIcon from "../../../assets/images/hidden-password.svg";

import "./UserAuth.scss";

const Login = () => {
  const { t: translation } = useTranslation();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(loginValidationSchema(translation)),
  });

  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [passwordAndRememberMe, setPasswordAndRememberMe] = useState({
    passwordVisible: false,
    rememberMe: false,
  });
  const [forgetPasswordModel, setForgetPasswordModel] = useState(false);

  /**
   * close forget password model
   */
  const closeForgetPasswordModel = (event: any) => {
    setForgetPasswordModel(false);
  };

  /**
   * login
   */
  const formRef = useRef<HTMLFormElement | null>(null);

  /*
   * Falls back to what is actually IN the inputs when react-hook-form's own
   * state is empty. That happens with Chrome autofill, which sets the DOM
   * value without a change event RHF can see. Only fills BLANKS - anything the
   * customer really typed is left exactly as RHF captured it.
   */
  const withAutofilledValues = (data: ILogin): ILogin => {
    const form = formRef.current;
    if (!form) {
      return data;
    }

    const readField = (name: string): string => {
      const input = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
      return input?.value ?? "";
    };

    return {
      ...data,
      email: data.email || readField("email"),
      password: data.password || readField("password"),
    };
  };

  const onLogin = async (data: ILogin) => {
    setLoading(true);
    if (passwordAndRememberMe.rememberMe) {
      storage.set("loginData", JSON.stringify(data));
    } else {
      storage.set("loginData", "");
    }
    const result = await login(data);
    if (result && result.data && result.data.success) {
      dispatch({
        type: ActionType.EMAILVALIDATED,
        payload: true,
      });
      navigate(ROUTES.EMAIL_VALIDATION, {
        state: {
          email: data.email,
          action: "login",
        },
      });
    } else {
      /*
       * Never default to "Email not found" (fixed 2026-08-27, bug B1).
       *
       * This used to report "Email not found" for ANY failure whose message
       * was not literally "Unauthorized" - including a WRONG PASSWORD on an
       * account that exists. Verified against the API: a real email with a bad
       * password returns "Oops! Wrong password." and a genuinely unknown email
       * returns "No user found." The server already distinguishes them
       * perfectly; the UI collapsed both into the wrong one.
       *
       * The cost is not just a bad string. Telling a customer their account
       * does not exist when it does sends them to sign up again, or to support.
       * It cost hours of debugging in this very session.
       *
       * The server's own message is preferred, since it is the only thing that
       * actually knows which case this is. The fallback is deliberately
       * NON-COMMITTAL rather than guessing at a cause.
       */
      const serverMessage: string | null =
        (result as any)?.error?.message ??
        (result as any)?.data?.message ??
        (result as any)?.message ??
        null;

      if (serverMessage && serverMessage !== "Unauthorized") {
        toastMessageError(serverMessage, "loginFailed");
      } else if (serverMessage === "Unauthorized") {
        toastMessageError(translation("incorrect_password"), "unauthorized");
      } else {
        toastMessageError(translation("login_failed_generic"), "loginFailed");
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    getRememberData();
  }, []);

  /**
   *  remember me functionality
   */
  const getRememberData = () => {
    const data = storage.getJSON("loginData");
    if (data) {
      reset({
        email: data.email,
        password: data.password,
      });
      setPasswordAndRememberMe((prevState) => ({
        ...prevState,
        rememberMe: true,
      }));
    }
  };

  return (
    <>
      <div className="userauth-main">
        <div className="container-fluid">
          <img src={Logo} alt="logo" className="logo" />

          <div className="userauth-form">
            <div className="userauth-top p-0">
              <h4 className="w-100">{translation("sign_in_to_skydive")}</h4>
            </div>

              {/*
                Chrome autofill writes the DOM directly and does not always
                fire the change event react-hook-form listens for, so the form
                could submit an EMPTY email while visibly showing one - the
                server then answered "No user found" and the customer was told
                their account did not exist (bug B2, and it cost hours in this
                session). Reading the real input values at submit time closes
                that gap without fighting the browser: RHF's values win when it
                has them, the DOM fills in when it does not.
              */}
            {window.location.pathname === ROUTES.LOGIN && (
              <form
                ref={formRef}
                onSubmit={handleSubmit((data) =>
                  onLogin(withAutofilledValues(data as ILogin))
                )}
              >
                <div className="form-group">
                  <InputWrapper
                    control={control}
                    name="email"
                    className="form-control"
                    placeholder="Email Address"
                    error={errors?.email}
                    type="email"
                  />
                </div>

                <div className="form-group">
                  <div className="input-icon">
                    <InputWrapper
                      control={control}
                      name="password"
                      className="form-control"
                      placeholder={translation("pass")}
                      error={errors?.password}
                      type={
                        passwordAndRememberMe.passwordVisible
                          ? typeText
                          : typePassword
                      }
                    />

                    <CommonButton
                      type="button"
                      onClick={() =>
                        setPasswordAndRememberMe((prevState) => ({
                          ...prevState,
                          passwordVisible:
                            !passwordAndRememberMe.passwordVisible,
                        }))
                      }
                      iconSrc={
                        passwordAndRememberMe.passwordVisible
                          ? EyeIcon
                          : EyeCloseIcon
                      }
                    />
                  </div>
                </div>

                <div className="forgot-check">
                  <div className="form-check m-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="check1"
                      name="option1"
                      value="something"
                      onChange={() =>
                        setPasswordAndRememberMe((prevState) => ({
                          ...prevState,
                          rememberMe: !passwordAndRememberMe.rememberMe,
                        }))
                      }
                      checked={passwordAndRememberMe.rememberMe}
                    />
                    <label className="form-check-label">
                      {translation("remember_me")}
                    </label>
                  </div>
                  <span onClick={() => setForgetPasswordModel(true)}>
                    {translation("forget_password")}
                  </span>
                </div>
                <CommonButton
                  className="theme-btn success-btn text-uppercase mt-35"
                  label={translation("admin_sign_in")}
                  loading={loading}
                  disabled={loading}
                />
              </form>
            )}
            {forgetPasswordModel && (
              <CommonModel closeModel={closeForgetPasswordModel} />
            )}
          </div>

          <p className="auth-bottom">
            {translation("donot_have_skydive_account")}{" "}
            <Link to={ROUTES.SIGNUP}>{translation("sign_up_here")}</Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default Login;
